import {
	type Diet,
	type ModifierGroup,
	type UpsertModifierGroupInput,
	upsertModifierGroupInputSchema,
	uuidv7,
} from "@luckymenu/core";
import { moveInList } from "./cache";
import { type FormErrors, paiseToRupees, rupeesToPaise, zodProblems } from "./form-errors";

/**
 * The add-on group form as both apps edit it: the group, its options, and
 * the items that offer it. Saved with `upsertModifierGroup`, then
 * `setModifierGroupItems` when the items changed.
 */

export type OptionDraft = {
	id: string;
	name: string;
	/** Rupees added to the price, as typed. Empty means free. */
	price: string;
	/** Null: no mark, as for a sauce. */
	diet: Diet | null;
	isAvailable: boolean;
};

export type AddonGroupDraft = {
	id: string;
	name: string;
	/** 0: optional. */
	minSelect: number;
	maxSelect: number;
	options: OptionDraft[];
	/** Items that offer the group, in menu order. */
	itemIds: string[];
};

export const ADDON_LIMITS = { name: 60, optionName: 60, options: 30, select: 30 } as const;

const blankOption = (): OptionDraft => ({
	id: uuidv7(),
	name: "",
	price: "",
	diet: null,
	isAvailable: true,
});

export function newAddonGroupDraft(id: string = uuidv7()): AddonGroupDraft {
	return { id, name: "", minSelect: 0, maxSelect: 1, options: [blankOption()], itemIds: [] };
}

export function addonGroupToDraft(
	group: ModifierGroup,
	itemIds: readonly string[],
): AddonGroupDraft {
	return {
		id: group.id,
		name: group.name,
		minSelect: group.minSelect,
		maxSelect: group.maxSelect,
		options: group.options.map((o) => ({
			id: o.id,
			name: o.name,
			price: o.priceDeltaPaise === 0 ? "" : paiseToRupees(o.priceDeltaPaise),
			diet: o.diet,
			isAvailable: o.isAvailable,
		})),
		itemIds: [...itemIds],
	};
}

export function addOption(draft: AddonGroupDraft): AddonGroupDraft {
	if (draft.options.length >= ADDON_LIMITS.options) return draft;
	return { ...draft, options: [...draft.options, blankOption()] };
}

/** Removes an option; the maximum shrinks with the list so the rule stays possible. */
export function removeOption(draft: AddonGroupDraft, index: number): AddonGroupDraft {
	const options = draft.options.filter((_, i) => i !== index);
	const maxSelect = Math.max(1, Math.min(draft.maxSelect, options.length));
	return { ...draft, options, maxSelect, minSelect: Math.min(draft.minSelect, maxSelect) };
}

export function updateOption(
	draft: AddonGroupDraft,
	index: number,
	change: Partial<Omit<OptionDraft, "id">>,
): AddonGroupDraft {
	return {
		...draft,
		options: draft.options.map((o, i) => (i === index ? { ...o, ...change } : o)),
	};
}

export function moveOption(draft: AddonGroupDraft, from: number, to: number): AddonGroupDraft {
	return { ...draft, options: moveInList(draft.options, from, to) };
}

/**
 * Sets how many options a diner picks, keeping min ≤ max within 0 to 30.
 * Raising the minimum above the maximum raises the maximum too.
 */
export function setSelect(
	draft: AddonGroupDraft,
	field: "minSelect" | "maxSelect",
	value: number,
): AddonGroupDraft {
	const v = Math.max(
		field === "maxSelect" ? 1 : 0,
		Math.min(ADDON_LIMITS.select, Math.round(value)),
	);
	if (field === "minSelect")
		return { ...draft, minSelect: v, maxSelect: Math.max(draft.maxSelect, v) };
	return { ...draft, maxSelect: v, minSelect: Math.min(draft.minSelect, v) };
}

/** Offers the group on the item, or stops. `menuOrder` keeps the list in menu order. */
export function toggleItem(
	draft: AddonGroupDraft,
	itemId: string,
	menuOrder: readonly string[],
): AddonGroupDraft {
	const chosen = new Set(draft.itemIds);
	if (chosen.has(itemId)) chosen.delete(itemId);
	else chosen.add(itemId);
	return { ...draft, itemIds: menuOrder.filter((id) => chosen.has(id)) };
}

export type AddonGroupFormResult =
	| {
			ok: true;
			input: UpsertModifierGroupInput;
			itemIds: string[];
	  }
	| { ok: false; errors: FormErrors };

export function addonGroupDraftToInput(draft: AddonGroupDraft): AddonGroupFormResult {
	const errors: FormErrors = {};
	const name = draft.name.trim();
	if (name === "") errors.name = "required";
	else if (name.length > ADDON_LIMITS.name) errors.name = "tooLong";
	if (draft.options.length === 0) errors.options = "required";
	const options: UpsertModifierGroupInput["options"] = [];
	draft.options.forEach((o, i) => {
		const optionName = o.name.trim();
		if (optionName === "") errors[`options.${i}.name`] = "required";
		else if (optionName.length > ADDON_LIMITS.optionName) errors[`options.${i}.name`] = "tooLong";
		const price = o.price.trim() === "" ? { paise: 0 } : rupeesToPaise(o.price);
		if ("problem" in price) errors[`options.${i}.price`] = price.problem;
		else
			options.push({
				id: o.id,
				name: optionName,
				priceDeltaPaise: price.paise,
				diet: o.diet,
				isAvailable: o.isAvailable,
			});
	});
	if (draft.minSelect > draft.maxSelect) errors.minSelect = "minMax";
	if (draft.options.length > 0 && draft.maxSelect > draft.options.length)
		errors.maxSelect = "maxOptions";
	if (Object.keys(errors).length > 0) return { ok: false, errors };

	const input: UpsertModifierGroupInput = {
		id: draft.id,
		name,
		minSelect: draft.minSelect,
		maxSelect: draft.maxSelect,
		options,
	};
	const parsed = upsertModifierGroupInputSchema.safeParse(input);
	if (!parsed.success) return { ok: false, errors: zodProblems(parsed.error) };
	return { ok: true, input, itemIds: draft.itemIds };
}
